'use client'
import {
    Fragment,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { ProdutListItem } from '@/eshop/components/ProductList/ListItem'
import { ProductListItem } from '@/eshop/components/ProductList/types'
import Pagination from '@/eshop/components/Pagination'
import { useQuery } from '@tanstack/react-query'
import {
    ProductGroupsConnectionEdge,
    ProductGroupSort_Field,
    SortOrder,
} from '@/eshop/gql/graphql'
import { getProductGroupsRequest, ProductGroupsResponse } from '@/eshop/queries'
import Loader from '@/eshop/components/Loader'
import { PAGE_SIZE } from '@/eshop/constants'
import { getFirstL, shouldRenderFirstLetter } from './ProductList/utils'
import { EshopContext, EshopContextCategory } from './EshopProvider'
import { useTranslation } from 'react-i18next'
import { Heading } from '@/components/Heading'
import { useK2Setup } from '../hooks/useK2Setup'

function getRequest(
    categoryId: string | null,
    languageId: number
): Promise<ProductGroupsResponse> {
    return getProductGroupsRequest({
        filter: [
            {
                products_categories_id: {
                    eq: categoryId,
                },
                languageId: {
                    eq: languageId,
                },
            },
        ],
        after: '',
        before: '',
        first: 1000,
        sort: [
            {
                field: ProductGroupSort_Field.SortingNameLang,
                order: SortOrder.Asc,
            },
        ],
    })
}

export interface ProductListProps {
    initialData: ProductGroupsResponse
    className?: string
    letter: string
    setLetter: (l: string) => void
}

export default function ProductList({
    initialData,
    className,
    letter,
    setLetter,
}: ProductListProps) {
    const [openRow, setOpenRow] = useState<ProductListItem['id'][]>([])
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(
        initialData?.productGroups?.totalCount
            ? Math.ceil(initialData.productGroups.totalCount / PAGE_SIZE)
            : 0
    )
    const { t } = useTranslation()
    const k2 = useK2Setup()
    const prevCategoryId = useRef<string | null>(null)

    const { categories, setCategories } = useContext(EshopContext)
    const selectedCategoryId = useMemo(() => {
        return categories.reduce<string | null>(
            (acc, cat) => cat?.id ?? acc,
            null
        )
    }, [categories])

    const [categoryId, selectedCategory] = useMemo(
        () =>
            categories.reduce(
                (acc, c) => {
                    if (c.id) {
                        return [c.id, c]
                    }
                    return acc
                },
                [null, undefined] as [
                    categoryId: string | null,
                    category: EshopContextCategory | undefined,
                ]
            ),
        [categories]
    )

    useEffect(() => {
        setOpenRow([])
    }, [categories])

    const { data, isFetching, isFetched } = useQuery({
        queryKey: ['product-groups', categoryId, k2.languageId, letter],
        queryFn: () => {
            return getRequest(categoryId, k2.languageId).then((data) => {
                const resultData = {
                    productGroups: {
                        ...data.productGroups,
                        edges: letter
                            ? (data.productGroups.edges?.sort((a, b) =>
                                  a.node.sortingNameLang >
                                  b.node.sortingNameLang
                                      ? 1
                                      : -1
                              ),
                              data.productGroups.edges?.filter((product) => {
                                  return filterByLetter(
                                      letter.toLowerCase(),
                                      product.node.sortingNameLang.toLowerCase()
                                  )
                              }))
                            : data.productGroups.edges,
                    },
                }

                if (
                    letter &&
                    (resultData.productGroups.edges?.length ?? 0) === 0 &&
                    (data.productGroups.edges?.length ?? 0) > 0 &&
                    prevCategoryId.current !== categoryId
                ) {
                    const newLetter = (
                        data.productGroups.edges?.[0]?.node.sortingNameLang?.charAt(
                            0
                        ) ?? 'A'
                    ).toUpperCase()
                    setLetter(newLetter)
                }

                prevCategoryId.current = categoryId

                return resultData
            }) as Promise<ProductGroupsResponse>
        },
        initialData,
    })

    function filterByLetter(letter: string, productName: string): any {
        if (letter === '0-#') {
            return productName.match(/^[0-9_.-]/)
        }
        const letterDiacritic = {
            c: 'č',
            r: 'ř',
            s: 'š',
            z: 'ž',
            a: 'á',
            e: 'é',
            i: 'í',
            o: 'ó',
            u: 'ú',
        }

        const result: boolean =
            letterDiacritic[letter as keyof typeof letterDiacritic] ===
            undefined
                ? productName.startsWith(letter)
                : productName.startsWith(letter) ||
                  productName.startsWith(
                      letterDiacritic[letter as keyof typeof letterDiacritic]
                  )

        return letter === 'c' ? result && !productName.startsWith('ch') : result
    }

    const sortedData = useMemo(
        (): ProductGroupsResponse => ({
            productGroups: {
                ...data.productGroups,
                edges: isFetching
                    ? []
                    : [...(data.productGroups.edges || [])]?.sort((a, b) => {
                          return a.node.sortingNameLang.localeCompare(
                              b.node.sortingNameLang,
                              'cs'
                          )
                      }),
            },
        }),
        [data, isFetching]
    )

    useEffect(() => {
        const totalCount = data?.productGroups?.edges?.length || 0
        if (selectedCategory && totalCount !== selectedCategory.productCount) {
            setCategories(
                categories.map((c) => {
                    if (c.id !== categoryId) {
                        return {
                            ...c,
                        }
                    }
                    return {
                        ...c,
                        productCount: isFetched ? totalCount : undefined,
                    }
                })
            )
        }
        setTotal(totalCount ? Math.ceil(totalCount / PAGE_SIZE) : 0)
    }, [
        categories,
        categoryId,
        data?.productGroups.edges,
        data.productGroups.totalCount,
        isFetched,
        selectedCategory,
        setCategories,
    ])

    const renderItem = useCallback(
        (
            edgeItem: ProductGroupsConnectionEdge,
            i: number,
            arr: ProductGroupsConnectionEdge[]
        ) => {
            const item = edgeItem.node
            const firstLetter = getFirstL(
                item.sortingNameLang,
                k2.languageId === 5
            )

            // Render first letter of Product if starts with different letter
            const renderLetter = shouldRenderFirstLetter(
                item.sortingNameLang,
                arr[i - 1]?.node?.sortingNameLang,
                i,
                k2.languageId === 5
            )

            const handleItemClick = () =>
                setOpenRow((state) =>
                    state.includes(item.id)
                        ? state.filter((id) => id !== item.id)
                        : [...state, item.id]
                )

            return (
                <Fragment key={item.id}>
                    {(i === 0 || renderLetter) && (
                        <ProductListLetter>{firstLetter}</ProductListLetter>
                    )}
                    <ProdutListItem
                        selectedCategoryId={selectedCategoryId}
                        item={item}
                        isOpen={openRow.includes(item.id)}
                        onClick={handleItemClick}
                    />
                </Fragment>
            )
        },
        [k2.languageId, selectedCategoryId, openRow]
    )

    return (
        <div className={className}>
            <Loader
                isLoading={
                    isFetching ||
                    (!!categoryId && isFetching) ||
                    (!data && !isFetched)
                }
            >
                <>
                    {selectedCategory && (
                        <div className="text-center">
                            <Heading
                                tag="h3"
                                align="center"
                                text={selectedCategory.name}
                            />
                            {!!selectedCategory?.productCount && (
                                <span>
                                    {selectedCategory.productCount}{' '}
                                    {t('eshop.productlist.productCount')}
                                </span>
                            )}
                        </div>
                    )}
                    {(
                        (k2.languageId === 5
                            ? sortedData
                            : data
                        )?.productGroups?.edges?.slice(
                            (page - 1) * PAGE_SIZE,
                            (page - 1) * PAGE_SIZE + PAGE_SIZE
                        ) || []
                    ).map(renderItem)}
                </>
            </Loader>
            {data?.productGroups?.edges?.length === 0 && (
                <div className="my-6 text-center">
                    {t('eshop.productlist.noProducts')}
                </div>
            )}
            {page > 0 && total > 0 && (
                <Pagination
                    onPageChange={setPage}
                    current={page}
                    total={total}
                    className="mt-24"
                />
            )}
        </div>
    )
}

export function ProductListLetter({ children }: PropsWithChildren<{}>) {
    return <div className="text-lg2 my-5 pl-2 desktop:pl-0">{children}</div>
}
